import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, BookOpen, Pencil, Trash2, ArrowRight } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonPlanList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lesson-plans`, { withCredentials: true });
      setPlans(data);
    } catch {
      toast.error("Failed to load lesson plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const openCreate = () => {
    setForm({ title: "", description: "" });
    setShowModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/lesson-plans`, form, { withCredentials: true });
      toast.success("Lesson plan created");
      setShowModal(false);
      navigate(`/admin/lesson-plans/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const p = deleteTarget;
    if (!p) return;
    try {
      await axios.delete(`${API}/lesson-plans/${p.id}`, { withCredentials: true });
      toast.success("Lesson plan deleted");
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lesson-plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="lesson-plans-title">
            Lesson Planner
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">
            Standalone instructional documents for instructors — not linked to Tracks, LEXPs, or cohorts.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="create-lesson-plan-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New Lesson Plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="lesson-plans-empty">
          <BookOpen className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-color, #4A90D9)" }} />
          <h3 className="font-body text-lg font-bold text-white mb-2">No lesson plans yet</h3>
          <p className="text-[#6E7787] font-body text-sm mb-4">Create your first plan to start authoring instructor content.</p>
          <Button
            onClick={openCreate}
            className="h-9 px-4 font-body text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Lesson Plan
          </Button>
        </div>
      ) : (
        <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="lesson-plans-table">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Title</th>
                <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Topics</th>
                <th className="text-right px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lesson-plan-row-${p.id}`}>
                  <td className="px-5 py-3.5 text-sm font-body font-medium text-white">
                    <button onClick={() => navigate(`/admin/lesson-plans/${p.id}`)} className="text-left hover:text-cyan-300" data-testid={`lesson-plan-title-${p.id}`}>
                      {p.title}
                    </button>
                    {p.description && <div className="text-xs text-[#6E7787] font-body mt-0.5 line-clamp-1">{p.description}</div>}
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge
                      className={`text-[10px] ${p.status === "published"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                        : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}
                      data-testid={`lesson-plan-status-${p.id}`}
                    >
                      {p.status === "published" ? "Published" : "Draft"}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-sm font-body text-[#A1A9B8]">{p.topic_count || 0}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/lesson-plans/${p.id}`)} className="h-7 px-2 text-[#6E7787] hover:text-white hover:bg-white/5" data-testid={`open-lesson-plan-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Open
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} className="h-7 w-7 p-0 text-[#6E7787] hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-lesson-plan-${p.id}`} title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="lesson-plan-create-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">New Lesson Plan</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Start with a title — you can add topics and tasks next.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Title <span className="text-red-400">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Week 3 — IV Medication Safety"
                data-testid="lesson-plan-title-input"
                required
                className="bg-[#0A0C10] border-white/10 text-white h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description for the instructor"
                data-testid="lesson-plan-description-input"
                className="bg-[#0A0C10] border-white/10 text-white h-10"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="lesson-plan-create-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="lesson-plan-create-submit"
                className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                Create & Open
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={2}
        entityName={deleteTarget?.title}
        entityType="lesson_plan"
        entityId={deleteTarget?.id}
        onConfirm={handleDelete}
      />
    </div>
  );
}
